<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-2">Loading address information...</p>
    </div>

    <!-- Address Forms -->
    <div v-else>
    <!-- Present Address -->
    <v-card variant="outlined" class="pa-2">
      <v-card-title class="text-subtitle-1 mb-1 pa-2">
        <v-icon class="me-1" size="small">mdi-home</v-icon>
        Present Address
      </v-card-title>
      <v-form ref="presentAddressForm" class="pa-1">
        <v-row class="ma-0">
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_village_house"
              label="Village/House"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_village_road_house_flat"
              label="Village/Road/House/Flat"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_post_office"
              label="Post Office"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_post_code"
              label="Post Code"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_division"
              label="Division"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_district"
              label="District"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.present_thana_upazilla"
              label="Thana/Upazilla"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- Permanent Address -->
    <v-card variant="outlined" class="pa-2 mt-1">
      <v-card-title class="text-subtitle-1 mb-1 pa-2">
        <v-icon class="me-1" size="small">mdi-map-marker</v-icon>
        Permanent Address
      </v-card-title>
      <v-form ref="permanentAddressForm" class="pa-1">
        <v-row class="ma-0">
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_village_house"
              label="Village/House"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_village_road_house_flat"
              label="Village/Road/House/Flat"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_post_office"
              label="Post Office"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_post_code"
              label="Post Code"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_division"
              label="Division"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_district"
              label="District"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="pa-1">
            <v-text-field
              v-model="localAddressInfo.permanent_thana_upazilla"
              label="Thana/Upazilla"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'

// Props
const props = defineProps({
  addressInfo: {
    type: Object,
    default: () => ({}) 
  },
  employeeId: {
     type: [String, Number],
    default: null
  }
})

// Emits
const emit = defineEmits(['update:addressInfo'])

// Store
const store = useStore()

// Local refs
const presentAddressForm = ref(null)
const permanentAddressForm = ref(null)
const loading = ref(false)
const saving = ref(false)

// Local address data
const localAddressInfo = ref({
  // Present Address
  present_division: '',
  present_district: '',
  present_thana_upazilla: '',
  present_address: '',
  present_village_house: '',
  present_village_road_house_flat: '',
  present_post_office: '',
  present_post_code: '',
  // Permanent Address
  permanent_division: '',
  permanent_district: '',
  permanent_thana_upazilla: '',
  permanent_address: '',
  permanent_village_house: '',
  permanent_village_road_house_flat: '',
  permanent_post_office: '',
  permanent_post_code: ''
})

watch(
  () => props.addressInfo,
  (newVal) => {
    if (newVal && typeof newVal === 'object') {
      // Merge only if incoming props have differences
      const hasChanges = Object.keys(newVal).some(
        key => newVal[key] !== localAddressInfo.value[key]
      )
      if (hasChanges) {
        localAddressInfo.value = { ...localAddressInfo.value, ...newVal }
      }
    }
  },
  { deep: true, immediate: true }
)

// Emit when local changes (to update parent)
watch(
  localAddressInfo,
  (newVal) => {
    emit('update:addressInfo', newVal)
  },
  { deep: true }
)

// Load address information
const dataLoaded = ref(false)
const loadAddressInfo = async () => {
  if (!props.employeeId || dataLoaded.value) return
  
  try {
    loading.value = true
    const response = await store.dispatch('addressInfo/fetchAddressInfoByEmployee', props.employeeId)
    
    if (response?.success && response?.data) {
      localAddressInfo.value = { ...localAddressInfo.value, ...response.data }
    }
    dataLoaded.value = true
  } catch (error) {
    console.error('Error loading address info:', error)
    // Don't show error toast for missing data, it's normal for new employees
  } finally {
    loading.value = false
  }
}

// Save address information
const saveAddressInfo = async () => {
  if (!props.employeeId) {
    throw new Error('Employee ID is required')
  }

  try {
    saving.value = true
    const response = await store.dispatch('addressInfo/saveEmployeeAddressInfo', {
      employeeId: props.employeeId,
      addressData: localAddressInfo.value
    })

    if (response.success) {
      localAddressInfo.value = { ...localAddressInfo.value, ...response.data }
      toast.success('Address information saved successfully')
    } else {
      throw new Error(response.message || 'Failed to save address information')
    }
  } catch (error) {
    console.error('Error saving address info:', error)
    throw error
  } finally {
    saving.value = false
  }
}

// Load data on mount
onMounted(() => {
  loadAddressInfo()
})

// Expose methods and computed properties
defineExpose({
  validatePresent: () => presentAddressForm.value?.validate(),
  validatePermanent: () => permanentAddressForm.value?.validate(),
  validateAll: async () => {
    const results = await Promise.all([
      presentAddressForm.value?.validate(),
      permanentAddressForm.value?.validate()
    ])
    return results.every(result => result?.valid !== false)
  },
  saveAddressInfo,
  loading: computed(() => loading.value),
  saving: computed(() => saving.value)
})
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
